import React, { useEffect, useMemo, useState } from "react";
import { api, extractError, fetchAllListResults } from "../lib/api";
import { useAuth, hasPermission } from "../context/AuthContext";
import { useLookups } from "../context/LookupOptionsContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { toast } from "sonner";
import { ChevronsUpDown } from "lucide-react";
import { parseNumberInput } from "../lib/numberInput";
import {
  isValidVehicleNumber,
  sanitizeVehicleNumberInput,
  VEHICLE_NUMBER_FORMAT_MSG,
  VEHICLE_NUMBER_PLACEHOLDER,
} from "../lib/vehicleNumber";
import { GatePassPhotoFields, uploadGatePhotos } from "./GatePassPhotos";

const EMPTY_FORM = {
  vehicle_number: "",
  driver_name: "",
  driver_phone: "",
  purpose: "INWARD_PADDY",
  party_id: "",
  party_name: "",
  expected_variety: "1509",
  expected_bags: "",
  notes: "",
  po_id: "",
};

export default function GatePassCreateForm({
  defaultPurpose = "INWARD_PADDY",
  lockedPurpose = null,
  onSuccess,
  onCancel,
  showCancel = true,
  compact = false,
  testIdPrefix = "gate",
}) {
  const { user } = useAuth();
  const { optionsFor } = useLookups();
  const varieties = optionsFor("paddy_variety");
  const [parties, setParties] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_FORM, purpose: lockedPurpose || defaultPurpose });
  const [plateFile, setPlateFile] = useState(null);
  const [vehicleFiles, setVehicleFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [openPos, setOpenPos] = useState([]);
  const [bookingSuggestions, setBookingSuggestions] = useState([]);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  const purpose = lockedPurpose || form.purpose;
  const isVisitor = purpose === "VISITOR";
  const isOutward = purpose === "OUTWARD_RICE";
  const isStore = purpose === "INWARD_STORE" || ["PACKING_MATERIAL", "CONSUMABLE", "SPARES", "FIXED_ASSET"].includes(purpose);

  useEffect(() => {
    if (!user) return;
    fetchAllListResults("/parties")
      .then(setParties)
      .catch((e) => toast.error(extractError(e)));
  }, [user]);

  useEffect(() => {
    if (!isStore) return;
    api.get("/store/grn/open-pos")
      .then((r) => setOpenPos(r.data?.results || []))
      .catch(() => setOpenPos([]));
  }, [isStore]);

  useEffect(() => {
    setForm((f) => ({ ...f, purpose: lockedPurpose || defaultPurpose }));
  }, [lockedPurpose, defaultPurpose]);

  const selectedParty = useMemo(
    () => parties.find((p) => String(p.id) === String(form.party_id)),
    [parties, form.party_id],
  );
  const isFarmerParty = selectedParty?.kind === "FARMER";

  const resetFields = () => {
    setForm({
      ...EMPTY_FORM,
      purpose: lockedPurpose || defaultPurpose,
    });
    setPlateFile(null);
    setVehicleFiles([]);
    setBookingSuggestions([]);
    setBookingDialogOpen(false);
    setPendingPayload(null);
  };

  const postGatePass = async (payload) => {
    const { data } = await api.post("/gate-passes", payload);
    if (data?.applied && data?.booking?.code) {
      toast.success(`Gate pass created — linked to booking ${data.booking.code}`);
    } else {
      toast.success(isVisitor ? "Visitor entry logged" : "Gate pass created");
    }
    resetFields();
    onSuccess?.();
  };

  const buildPayload = async () => {
    const vehicle = sanitizeVehicleNumberInput(form.vehicle_number);
    const vehicleForCode = vehicle || "pending";
    const photoFields = await uploadGatePhotos({
      plateFile,
      vehicleFiles,
      entityCode: vehicleForCode,
    });
    const payload = isVisitor
      ? {
          vehicle_number: vehicle || "WALKIN",
          driver_name: form.driver_name,
          driver_phone: form.driver_phone || undefined,
          purpose: "VISITOR",
          notes: form.notes.trim() || undefined,
          ...photoFields,
        }
      : isOutward
      ? {
          vehicle_number: vehicle,
          driver_name: form.driver_name,
          driver_phone: form.driver_phone || undefined,
          purpose: "OUTWARD_RICE",
          ...photoFields,
        }
      : isStore
      ? {
          vehicle_number: vehicle,
          driver_name: form.driver_name,
          driver_phone: form.driver_phone || undefined,
          purpose: "INWARD_STORE",
          party_id: form.party_id ? +form.party_id : undefined,
          po_id: form.po_id ? +form.po_id : undefined,
          ...photoFields,
        }
      : {
          vehicle_number: vehicle,
          driver_name: form.driver_name,
          driver_phone: form.driver_phone || undefined,
          purpose: form.purpose,
          party_id: form.party_id ? +form.party_id : undefined,
          ...(isFarmerParty ? {} : { expected_variety: form.expected_variety }),
          expected_bags: parseNumberInput(form.expected_bags, { integer: true }),
          ...photoFields,
        };
    if (!payload.number_plate_snapshot_uri) delete payload.number_plate_snapshot_uri;
    if (!payload.vehicle_photos?.length) delete payload.vehicle_photos;
    return payload;
  };

  const create = async (e) => {
    e.preventDefault();
    const vehicle = sanitizeVehicleNumberInput(form.vehicle_number);
    if (!isValidVehicleNumber(vehicle, { allowEmpty: isVisitor })) {
      toast.error(VEHICLE_NUMBER_FORMAT_MSG);
      return;
    }
    try {
      setSubmitting(true);
      const payload = await buildPayload();
      if (isOutward) {
        const { data: match } = await api.get("/outward-truck-bookings/match", {
          params: { vehicle },
        });
        if (!match?.exact && (match?.suggestions || []).length) {
          setPendingPayload(payload);
          setBookingSuggestions(match.suggestions);
          setBookingDialogOpen(true);
          return;
        }
      }
      await postGatePass(payload);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmBooking = async (bookingId) => {
    if (!pendingPayload) return;
    try {
      setSubmitting(true);
      await postGatePass({ ...pendingPayload, confirm_booking_id: bookingId });
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
      setBookingDialogOpen(false);
      setPendingPayload(null);
      setBookingSuggestions([]);
    }
  };

  const skipBookingMatch = async () => {
    if (!pendingPayload) return;
    try {
      setSubmitting(true);
      await postGatePass(pendingPayload);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
      setBookingDialogOpen(false);
      setPendingPayload(null);
      setBookingSuggestions([]);
    }
  };

  const gridClass = compact
    ? "grid grid-cols-1 gap-4"
    : "grid grid-cols-1 md:grid-cols-3 gap-4";

  return (
    <form onSubmit={create} className={gridClass} data-testid={`${testIdPrefix}-form`}>
      {!lockedPurpose && (
        <Field label="Purpose">
          <Select
            value={form.purpose}
            onValueChange={(v) =>
              setForm((f) => ({
                ...f,
                purpose: v,
                ...(v === "VISITOR"
                  ? { party_id: "", party_name: "", expected_variety: "", expected_bags: "" }
                  : { notes: "", expected_variety: f.expected_variety || "1509", expected_bags: f.expected_bags ?? "" }),
              }))
            }
          >
            <SelectTrigger data-testid={`${testIdPrefix}-purpose-select`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="INWARD_PADDY">Inward — Paddy</SelectItem>
              <SelectItem value="INWARD_STORE">Inward — Store (packing / spares / assets)</SelectItem>
              <SelectItem value="OUTWARD_RICE">Outward — Rice</SelectItem>
              <SelectItem value="VISITOR">Visitor</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      )}
      {isVisitor ? (
        <>
          <Field label="Visitor Name" required>
            <Input
              value={form.driver_name}
              onChange={(e) => setForm({ ...form, driver_name: e.target.value })}
              required
              data-testid={`${testIdPrefix}-visitor-name-input`}
            />
          </Field>
          <Field label="Visitor Phone">
            <Input
              value={form.driver_phone}
              onChange={(e) => setForm({ ...form, driver_phone: e.target.value })}
              data-testid={`${testIdPrefix}-visitor-phone-input`}
            />
          </Field>
          <Field label="Vehicle Number">
            <Input
              value={form.vehicle_number}
              onChange={(e) =>
                setForm({ ...form, vehicle_number: sanitizeVehicleNumberInput(e.target.value) })
              }
              placeholder={`Optional — ${VEHICLE_NUMBER_PLACEHOLDER}`}
              autoCapitalize="characters"
              spellCheck={false}
              data-testid={`${testIdPrefix}-vehicle-input`}
            />
            <p className="text-[10px] text-muted-foreground mt-1">Letters and digits only — no spaces or symbols.</p>
          </Field>
          <Field label="Visit Notes" className={compact ? "" : "md:col-span-2"}>
            <Input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Person to meet, department, purpose of visit…"
              data-testid={`${testIdPrefix}-visitor-notes-input`}
            />
          </Field>
        </>
      ) : (
        <>
          <Field label="Vehicle Number" required>
            <Input
              value={form.vehicle_number}
              onChange={(e) =>
                setForm({ ...form, vehicle_number: sanitizeVehicleNumberInput(e.target.value) })
              }
              placeholder={VEHICLE_NUMBER_PLACEHOLDER}
              required
              autoCapitalize="characters"
              spellCheck={false}
              data-testid={`${testIdPrefix}-vehicle-input`}
            />
            <p className="text-[10px] text-muted-foreground mt-1">Letters and digits only — no spaces or symbols.</p>
          </Field>
          <Field label="Driver Name" required>
            <Input
              value={form.driver_name}
              onChange={(e) => setForm({ ...form, driver_name: e.target.value })}
              required
              data-testid={`${testIdPrefix}-driver-input`}
            />
          </Field>
          <Field label="Driver Phone">
            <Input
              value={form.driver_phone}
              onChange={(e) => setForm({ ...form, driver_phone: e.target.value })}
              data-testid={`${testIdPrefix}-phone-input`}
            />
          </Field>
          {!isOutward && !isStore && (
            <>
          <Field label="Party">
            <PartyPicker
              user={user}
              parties={parties}
              setParties={setParties}
              partyId={form.party_id}
              onSelectParty={(id, displayName) => {
                const party = parties.find((p) => String(p.id) === String(id));
                setForm((f) => ({
                  ...f,
                  party_id: id ? String(id) : "",
                  party_name: displayName ?? "",
                  ...(party?.kind === "FARMER" ? { expected_variety: "" } : {}),
                }));
              }}
              onClear={() => setForm((f) => ({ ...f, party_id: "", party_name: "", expected_variety: f.expected_variety || "1509" }))}
              testIdPrefix={testIdPrefix}
            />
          </Field>
          {!isFarmerParty && (
            <Field label="Expected Variety">
              <Select value={form.expected_variety} onValueChange={(v) => setForm({ ...form, expected_variety: v })}>
                <SelectTrigger data-testid={`${testIdPrefix}-variety-select`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {varieties.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Expected Bags">
            <Input
              type="number"
              value={form.expected_bags}
              onChange={(e) => setForm({ ...form, expected_bags: e.target.value })}
              data-testid={`${testIdPrefix}-bags-input`}
            />
          </Field>
            </>
          )}
          {isStore && (
            <Field label="Purchase order (optional)" className={compact ? "" : "md:col-span-2"}>
              <Select
                value={form.po_id || "__none__"}
                onValueChange={(v) => {
                  const po = openPos.find((p) => String(p.id) === String(v));
                  setForm((f) => ({
                    ...f,
                    po_id: v === "__none__" ? "" : v,
                    party_id: po?.supplier_id ? String(po.supplier_id) : f.party_id,
                    party_name: po?.supplier_name || f.party_name,
                    purpose: "INWARD_STORE",
                  }));
                }}
              >
                <SelectTrigger data-testid={`${testIdPrefix}-po-select`}><SelectValue placeholder="No PO — store receives ad-hoc" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No PO — receive now, link PO later</SelectItem>
                  {openPos.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.po_number} · {p.supplier_name} · {(p.category || "").replaceAll("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">
                Without a PO, stock posts immediately and procurement links the PO for rates later.
              </p>
            </Field>
          )}
          {isOutward && (
            <p className={`text-xs text-muted-foreground ${compact ? "" : "md:col-span-2"}`}>
              Party and sales contract will be linked by the sales supervisor after gate entry.
            </p>
          )}
        </>
      )}
      <GatePassPhotoFields
        plateFile={plateFile}
        onPlateFile={setPlateFile}
        vehicleFiles={vehicleFiles}
        onVehicleFiles={setVehicleFiles}
        testIdPrefix={testIdPrefix}
        className={compact ? "" : "md:col-span-3"}
      />
      <div className={`${compact ? "" : "md:col-span-3"} flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2`}>
        {showCancel && onCancel && (
          <Button type="button" variant="outline" className="w-full sm:w-auto min-h-[44px]" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          className="bg-bansal-sage hover:bg-bansal-sageDark text-white w-full sm:w-auto min-h-[44px]"
          data-testid={`${testIdPrefix}-submit-button`}
          disabled={submitting}
        >
          {submitting ? "Saving…" : isVisitor ? "Log visitor in" : "Issue gate pass"}
        </Button>
      </div>
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Did you mean?</DialogTitle>
            <DialogDescription>
              This plate is close to a pre-booked outward truck. Select the booking to auto-link sales, or continue without a match.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {bookingSuggestions.map((b) => (
              <button
                key={b.id}
                type="button"
                className="w-full text-left rounded-lg border border-border px-3 py-3 hover:border-primary/50 hover:bg-accent/50 transition-colors"
                onClick={() => confirmBooking(b.id)}
                disabled={submitting}
              >
                <p className="font-semibold tracking-wide">{b.vehicle_number}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {b.party_name || "—"} · {b.sales_contract_number || b.code}
                  {b.transporter_name ? ` · ${b.transporter_name}` : ""}
                </p>
              </button>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={skipBookingMatch} disabled={submitting}>
              Continue without booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
}

function PartyPicker({ user, parties, setParties, partyId, onSelectParty, onClear, testIdPrefix }) {
  const [comboOpen, setComboOpen] = useState(false);
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newPartyName, setNewPartyName] = useState("");
  const [newPartyPhone, setNewPartyPhone] = useState("");
  const [creatingParty, setCreatingParty] = useState(false);
  const canCreateFarmer = hasPermission(user, "gate.farmer.create");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return parties;
    return parties.filter((f) => (f.display_name || "").toLowerCase().includes(s));
  }, [parties, q]);

  const selectedRow = useMemo(() => parties.find((f) => String(f.id) === String(partyId)), [parties, partyId]);
  const selectedLabel = selectedRow?.display_name || "";

  const openCreateFarmerDialog = () => {
    const name = q.trim();
    if (!name) return;
    setNewPartyName(name);
    setNewPartyPhone("");
    setCreateOpen(true);
  };

  const createFarmer = async () => {
    const name = newPartyName.trim();
    const phone = newPartyPhone.trim();
    try {
      if (!name) return;
      if (!phone) {
        toast.error("Phone number is required");
        return;
      }
      setCreatingParty(true);
      const r = await api.post("/parties", { kind: "FARMER", display_name: name, phone });
      const row = r.data;
      setParties((prev) =>
        [...prev, row].sort((a, b) => String(a.display_name).localeCompare(String(b.display_name))),
      );
      onSelectParty(row.id, row.display_name);
      setCreateOpen(false);
      setComboOpen(false);
      setQ("");
      setNewPartyName("");
      setNewPartyPhone("");
      toast.success("Farmer registered");
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setCreatingParty(false);
    }
  };

  return (
    <div className="space-y-2">
      <Popover open={comboOpen} onOpenChange={setComboOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="w-full justify-between font-normal gap-2" data-testid={`${testIdPrefix}-party-combobox`}>
            <span className="truncate flex-1 text-left font-normal text-foreground">
              {selectedLabel || <span className="text-muted-foreground">Search or select party…</span>}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search party…" value={q} onValueChange={setQ} />
            <CommandList>
              <CommandEmpty>
                {canCreateFarmer && q.trim() ? (
                  <button type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm" onClick={openCreateFarmerDialog}>
                    Register farmer “{q.trim()}”
                  </button>
                ) : (
                  <span className="text-xs text-muted-foreground px-3 py-2 block">No farmer matches.</span>
                )}
              </CommandEmpty>
              <CommandGroup>
                {filtered.map((f) => (
                  <CommandItem
                    key={f.id}
                    value={String(f.id)}
                    onSelect={() => {
                      onSelectParty(f.id, f.display_name);
                      setComboOpen(false);
                      setQ("");
                    }}
                  >
                    {f.display_name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {partyId ? (
        <Button type="button" variant="ghost" size="sm" className="text-xs h-8 px-0" onClick={onClear}>
          Clear party selection
        </Button>
      ) : null}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add farmer</DialogTitle>
            <DialogDescription>
              Party type will be saved as FARMER. Enter phone number to register this farmer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Farmer name</Label>
              <Input value={newPartyName} disabled className="mt-1.5" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Phone number</Label>
              <Input
                value={newPartyPhone}
                onChange={(e) => setNewPartyPhone(e.target.value)}
                placeholder="10-digit phone"
                className="mt-1.5"
                data-testid={`${testIdPrefix}-new-farmer-phone`}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={creatingParty}>
              Cancel
            </Button>
            <Button type="button" className="bg-bansal-sage hover:bg-bansal-sageDark text-white" onClick={createFarmer} disabled={creatingParty}>
              {creatingParty ? "Saving..." : "Save farmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, required, className, children }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
